import { useRef, useCallback } from 'react';

const lastPointer = { x: 0, y: 0 };
let pointerTracking = false;

function trackPointer(e) {
  lastPointer.x = e.clientX;
  lastPointer.y = e.clientY;
}

function ensurePointerTracking() {
  if (pointerTracking || typeof window === 'undefined') return;
  pointerTracking = true;
  window.addEventListener('pointermove', trackPointer, { passive: true });
  window.addEventListener('mousemove', trackPointer, { passive: true });
}

function useTilt(maxAngle = 12, zRaise = 15) {
  const ref = useRef(null);
  const bgRef = useRef(null);
  const fgRef = useRef(null);
  const tiltStateRef = useRef({ rx: 0, ry: 0, x: 0, y: 0, active: false });
  const pendingSmoothTiltRef = useRef(false);
  const smoothEnterUntilRef = useRef(0);

  const flatTransform = 'perspective(500px) rotateX(0deg) rotateY(0deg)';
  const flatFgTransform = `perspective(500px) rotateX(0deg) rotateY(0deg) translate(0px, 0px)`;

  const getTargets = useCallback(() => {
    const bg = bgRef.current;
    const fg = fgRef.current;
    return (bg || fg) ? [bg, fg].filter(Boolean) : [ref.current].filter(Boolean);
  }, []);

  const applyTiltAtClient = useCallback((clientX, clientY) => {
    const el = ref.current;
    if (!el) return;
    const { left, top, width, height } = el.getBoundingClientRect();
    const x = (clientX - left) / width - 0.5;
    const y = (clientY - top) / height - 0.5;
    const rx = y * maxAngle;
    const ry = -x * maxAngle;
    tiltStateRef.current = {
      rx,
      ry,
      x,
      y,
      active: true,
      version: (tiltStateRef.current.version || 0) + 1,
    };

    const bg = bgRef.current;
    const fg = fgRef.current;

    if (bg || fg) {
      if (bg) bg.style.transform = `perspective(500px) rotateX(${rx}deg) rotateY(${ry}deg)`;
      if (fg) fg.style.transform = `perspective(500px) rotateX(${rx}deg) rotateY(${ry}deg) translate(${-x * zRaise}px, ${-y * zRaise}px)`;
    } else {
      el.style.transform = `perspective(500px) rotateX(${rx}deg) rotateY(${ry}deg)`;
    }
  }, [maxAngle, zRaise]);

  const applySmoothTiltAtClient = useCallback((clientX, clientY) => {
    const targets = getTargets();
    if (!targets.length) return false;
    const el = ref.current;
    const bg = bgRef.current;
    const fg = fgRef.current;
    for (const target of targets) {
      target.style.transition = 'none';
      if (target === bg) {
        target.style.transform = flatTransform;
      } else if (target === fg) {
        target.style.transform = flatFgTransform;
      } else {
        target.style.transform = flatTransform;
      }
    }
    if (el) void el.offsetHeight;
    smoothEnterUntilRef.current = Date.now() + 400;
    for (const target of targets) {
      target.style.transition = 'transform 0.4s ease-out';
    }
    applyTiltAtClient(clientX, clientY);
    if (el) void el.offsetHeight;
    setTimeout(() => {
      smoothEnterUntilRef.current = 0;
      for (const target of targets) {
        if (target) target.style.transition = 'transform 0.05s ease-out';
      }
    }, 400);
    return true;
  }, [applyTiltAtClient, flatFgTransform, flatTransform, getTargets]);

  const isPointerOverElement = useCallback((el) => {
    if (!el) return false;
    const hit = document.elementFromPoint(lastPointer.x, lastPointer.y);
    return !!(hit && el.contains(hit));
  }, []);

  const onMouseMove = useCallback((e) => {
    ensurePointerTracking();
    if (pendingSmoothTiltRef.current) {
      pendingSmoothTiltRef.current = false;
      applySmoothTiltAtClient(e.clientX, e.clientY);
      return;
    }
    const el = ref.current;
    const targets = getTargets();
    const unprepped = targets.some((target) => !target.style.transition);
    if (unprepped && el && isPointerOverElement(el)) {
      applySmoothTiltAtClient(e.clientX, e.clientY);
      return;
    }
    applyTiltAtClient(e.clientX, e.clientY);
  }, [applySmoothTiltAtClient, applyTiltAtClient, getTargets, isPointerOverElement]);

  const transitionDuration = 0.15;

  const onMouseEnter = useCallback(() => {
    ensurePointerTracking();
    if (Date.now() < smoothEnterUntilRef.current) return;
    pendingSmoothTiltRef.current = false;
    const targets = getTargets();
    for (const el of targets) {
      el.style.transition = `transform ${transitionDuration}s ease-out`;
    }
    setTimeout(() => {
      for (const el of targets) {
        if (el) el.style.transition = 'transform 0.05s ease-out';
      }
    }, transitionDuration * 1000);
  }, [getTargets]);

  const onMouseLeave = useCallback(() => {
    pendingSmoothTiltRef.current = false;
    tiltStateRef.current = { rx: 0, ry: 0, x: 0, y: 0, active: false };
    const targets = getTargets();
    for (const el of targets) {
      el.style.transition = 'transform 0.4s ease-out';
      el.style.transform = '';
    }
  }, [getTargets]);

  const resetTilt = useCallback(() => {
    pendingSmoothTiltRef.current = false;
    tiltStateRef.current = { rx: 0, ry: 0, x: 0, y: 0, active: false };
    const targets = getTargets();
    for (const el of targets) {
      if (!el) continue;
      el.style.transition = 'transform 0.4s ease-out';
      el.style.transform = '';
    }
  }, [getTargets]);

  const enterFromPointerIfInside = useCallback(() => {
    ensurePointerTracking();
    const el = ref.current;
    if (!el || !getTargets().length || !isPointerOverElement(el)) {
      pendingSmoothTiltRef.current = true;
      return false;
    }
    pendingSmoothTiltRef.current = false;
    applySmoothTiltAtClient(lastPointer.x, lastPointer.y);
    return true;
  }, [applySmoothTiltAtClient, getTargets, isPointerOverElement]);

  const armSmoothTiltEntry = useCallback(() => {
    pendingSmoothTiltRef.current = true;
  }, []);

  return {
    ref,
    bgRef,
    fgRef,
    tiltStateRef,
    onMouseMove,
    onMouseEnter,
    onMouseLeave,
    resetTilt,
    enterFromPointerIfInside,
    armSmoothTiltEntry,
  };
}

export default useTilt;
